/**
 * Shared Gobodo record query hooks.
 *
 * Entity-level query keys (`['gobodo', 'records', objectApiName]`) allow
 * React Query to deduplicate fetches across components.  Previously every
 * component had its own key + inline Promise.all, causing the same object
 * type to be fetched 3-4x on every page load.
 *
 * Server-side cache: getRecordsForObject is called with cachekey/cacheduration
 * so the backend (gobodo-api-promise) can cache getObjectsInGraphByType per object type.
 */

import { useQuery } from '@tanstack/react-query';
import { getRecordsForObject, getRecordCount, type GenericRecord } from '../recordStorageService';
import type { GobodoCacheOpts } from '../gobodo/gobodoRecordService';
import { shouldUseGobodo } from '../../utils/gobodoUtils';

export const gobodoRecordKeys = {
  all: ['gobodo', 'records'] as const,
  byType: (objectApiName: string) => [...gobodoRecordKeys.all, objectApiName] as const,
  count: (objectApiName: string) => ['gobodo', 'records', 'count', objectApiName] as const,
};

/** Server-side cache options for background record lists (Task, Case, Client, Contact, Lead, Opportunity, etc.). Align with React Query staleTime. */
function getRecordCacheOpts(objectApiName: string): GobodoCacheOpts {
  return {
    cachekey: ['gobodo-records', objectApiName],
    cacheduration: '60',
  };
}

export function useGobodoRecords(
  objectApiName: string,
  options?: { enabled?: boolean }
) {
  const gobodoEnabled = shouldUseGobodo();
  return useQuery<GenericRecord[]>({
    queryKey: gobodoRecordKeys.byType(objectApiName),
    queryFn: () =>
      getRecordsForObject(objectApiName, {
        cache: getRecordCacheOpts(objectApiName),
      }),
    enabled: options?.enabled === undefined ? gobodoEnabled : (gobodoEnabled && options.enabled),
    staleTime: 60_000,
  });
}

/**
 * Fetch only the record count for an object type (count endpoint).
 * Use for count-only UI (badges, totals) to avoid fetching full lists with getRecordsForObject.
 */
export function useRecordCount(objectApiName: string) {
  return useQuery<number>({
    queryKey: gobodoRecordKeys.count(objectApiName),
    queryFn: () => getRecordCount(objectApiName),
    enabled: !!objectApiName,
    staleTime: 60_000,
  });
}
